import React, { useEffect, useRef, useState } from "react";
import { getApi, postApi } from "../AppApis";
import { useLocation, useNavigate } from "react-router-dom";
import Header from "../Header/Header";
import Balls from "../Templates/Loading";
import { showErrorToast } from "../utils";
import CG_SingleQuestion from "./CG_SingleQuestion";

export default function CG_Questions() {
    const navigate = useNavigate();
    const location = useLocation();
    const documents = location.state?.document?.documents;
    const document_ids = Array.isArray(documents) ? documents.map((item) => item.id) : [document.id];
    // const document_ids = ["6752921dab2bb388eaa47c03"];

    const [questionsArray, setQuestionsArray] = useState([]);
    const [currentDocumentIndex, setCurrentDocumentIndex] = useState(0);
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [answers, setAnswers] = useState([]);
    const [doc_url, setDoc_url] = useState(null);
    const [isAnswerLoading, setIsAnswerLoading] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [resetTrigger, setResetTrigger] = useState(0);

    const [isQuestionsAvailable, setIsQuestionsAvailable] = useState(true);
    const [allDocuments, setAllDocuments] = useState([]);
    const [allDocumentAnswers, setAllDocumentAnswers] = useState([]); // All documents answers

    const currentDocument = questionsArray[currentDocumentIndex];
    const currentQuestion = currentDocument?.questions[currentQuestionIndex];

    useEffect(() => {
        if (currentQuestion && currentQuestion.length > 0) {
            const filteredQuestions = currentQuestion.filter((q) => q.text !== "signature");
            const initialAnswers = filteredQuestions.map((q) => ({
                question_id: q.question_id,
                answer: "",
                doc_url: currentDocument.doc_url,
            }));
            setAnswers(initialAnswers);
        }
    }, [currentQuestion, questionsArray]);

    // Load existing answers when navigating between questions
    useEffect(() => {
        if (currentDocument && currentQuestion) {
            const existingDocAnswers = allDocumentAnswers.find((docAnswer) => docAnswer.doc_id === currentDocument.document_id);

            if (existingDocAnswers) {
                const existingQuestionAnswers = existingDocAnswers.answers.filter((ans) =>
                    currentQuestion.some((q) => q.question_id === ans.question_id)
                );

                if (existingQuestionAnswers.length > 0) {
                    const filteredQuestions = currentQuestion.filter((q) => q.text !== "signature");
                    const loadedAnswers = filteredQuestions.map((q) => {
                        const existingAnswer = existingQuestionAnswers.find((ans) => ans.question_id === q.question_id);
                        return {
                            question_id: q.question_id,
                            answer: existingAnswer ? existingAnswer.answer : "",
                            doc_url: currentDocument.doc_url,
                        };
                    });
                    setAnswers(loadedAnswers);
                }
            }
        }
    }, [currentDocumentIndex, currentQuestionIndex, allDocumentAnswers]);

    useEffect(() => {
        getQuestions();
    }, []);

    const getQuestions = async () => {
        try {
            setIsLoading(true);
            const endpoint = `/documents/get_multiple_documents_questions?doc_ids=${document_ids?.join(",")}`;

            let response = await getApi(endpoint);
            response = response.response.data;
            const documentsArray = response?.documents || [];
            if (response?.summary?.total_questions === 0) {
                setIsQuestionsAvailable(false);
            }
            let documentsNonEmptyQuestions = documentsArray.filter((doc) => doc.questions && doc.questions.length > 0);

            const onlyDocuments = documentsNonEmptyQuestions.map((doc) => doc.doc_url);
            setAllDocuments(onlyDocuments);
            setQuestionsArray(documentsNonEmptyQuestions);

            // Initialize allDocumentAnswers structure
            const initialDocumentAnswers = documentsNonEmptyQuestions.map((doc) => ({
                doc_id: doc.document_id,
                answers: [],
            }));
            setAllDocumentAnswers(initialDocumentAnswers);
        } catch (error) {
            console.log("Error in getquestions::", error);
        } finally {
            setIsLoading(false);
        }
    };

    const storeCurrentAnswers = () => {
        const updatedAnswers = [...allDocumentAnswers]; // use current state directly
        const docIndex = updatedAnswers.findIndex((doc) => doc.doc_id === currentDocument.document_id);

        if (docIndex !== -1) {
            const currentQuestionIds = currentQuestion.map((q) => q.question_id);
            updatedAnswers[docIndex].answers = updatedAnswers[docIndex].answers.filter((ans) => !currentQuestionIds.includes(ans.question_id));

            const validAnswers = answers
                .filter((ans) => ans.answer !== "")
                .map((ans) => ({
                    question_id: ans.question_id,
                    answer: ans.answer,
                }));

            updatedAnswers[docIndex].answers.push(...validAnswers);
        }

        // update the state
        setAllDocumentAnswers(updatedAnswers);

        // return the updated object immediately
        return updatedAnswers;
    };

    const submitAllAnswers = async (allQuestions) => {
        try {
            setIsAnswerLoading(true);

            // Prepare the final payload
            const payload = {
                documents: allQuestions.map((docAnswer) => ({
                    doc_id: docAnswer.doc_id,
                    answers: docAnswer.answers,
                })),
            };

            console.log("Final payload:", payload);

            const response = await postApi("/documents/save_multiple_answers", payload);

            if (response.error) {
                return showErrorToast(response.error, {
                    autoClose: 3000,
                    pauseOnHover: false,
                });
            } else if (response["server-error"]) {
                return showErrorToast(response["server-error"], {
                    autoClose: 3000,
                    pauseOnHover: false,
                });
            }

            // Navigate to success page
            navigate("/contract-generation/contract-submitted", {
                state: {
                    document: response,
                    document_id: document_ids,
                    question_id: "",
                },
            });
        } catch (error) {
            console.log("Error in submitAllAnswers::", error);
            showErrorToast("Error submitting answers", {
                autoClose: 3000,
                pauseOnHover: false,
            });
        } finally {
            setIsAnswerLoading(false);
        }
    };

    const onNext = async () => {
        const checkboxQuestions = currentQuestion?.filter((question) => question.type === "checkbox");
        const checkboxQuestionIds = checkboxQuestions.map((question) => question.question_id);
        const filteredAnswers = answers.filter((ans) => !checkboxQuestionIds?.includes(ans.question_id));
        const nonCheckboxAllAnswersFilled = filteredAnswers.every((ans) => ans.answer !== "");

        if (!nonCheckboxAllAnswersFilled) {
            alert("Please fill all the fields");
            return;
        }

        // Store current answers
        const allQuestions = storeCurrentAnswers();

        const isLastQuestion = currentQuestionIndex === currentDocument.questions.length - 1;
        const isLastDocument = currentDocumentIndex === questionsArray.length - 1;

        if (isLastQuestion && isLastDocument) {
            // This is the last question of the last document - submit all answers
            await submitAllAnswers(allQuestions);
        } else if (isLastQuestion) {
            // Move to next document
            setCurrentDocumentIndex(currentDocumentIndex + 1);
            setCurrentQuestionIndex(0);
            setAnswers([]);
            setResetTrigger(resetTrigger + 1); // Trigger reset in child components
        } else {
            // Move to next question
            setCurrentQuestionIndex(currentQuestionIndex + 1);
            setAnswers([]);
        }
    };

    const totalQuestions = questionsArray.reduce((count, doc) => {
        return count + doc.questions.length;
    }, 0);

    const getCurrentQuestionNumber = () => {
        let questionNumber = 1; // Start from 1
        for (let i = 0; i < currentDocumentIndex; i++) {
            questionNumber += questionsArray[i].questions.length;
        }
        return questionNumber + currentQuestionIndex;
    };

    const onPrev = () => {
        // Store current answers before navigating
        storeCurrentAnswers();

        if (currentQuestionIndex > 0) {
            setCurrentQuestionIndex(currentQuestionIndex - 1);
        } else if (currentDocumentIndex > 0) {
            setCurrentDocumentIndex(currentDocumentIndex - 1);
            setCurrentQuestionIndex(questionsArray[currentDocumentIndex - 1].questions.length - 1);
        }
        setAnswers([]);
    };

    const handleChangeTextAndCheckbox = (text, questionId) => {
        let copyAnswer = [...answers];
        const index = copyAnswer.findIndex((ans) => ans.question_id === questionId);
        copyAnswer[index].answer = text;
        setAnswers(copyAnswer);
    };

    return (
        <div className="tw-bg-background_color tw-h-screen tw-flex tw-flex-col tw-pt-[70px]">
            <Header title="Contract Generation" />
            {isLoading ? (
                <div className="tw-w-full tw-h-full tw-flex tw-justify-center tw-items-center">
                    <Balls />
                </div>
            ) : isQuestionsAvailable ? (
                <div className="tw-flex tw-flex-col  tw-p-6 tw-overflow-y-auto tw-items-center ">
                    {currentQuestion?.length > 0 && (
                        <div className="tw-w-full tw-max-w-3xl tw-mx-auto">
                            {currentQuestion.map((item, index) => (
                                <CG_SingleQuestion
                                    key={index}
                                    answers={answers}
                                    setResetTrigger={setResetTrigger}
                                    resetTrigger={resetTrigger}
                                    index={index}
                                    item={item}
                                    handleChangeTextAndCheckbox={handleChangeTextAndCheckbox}
                                />
                            ))}
                        </div>
                    )}
                    <div className="tw-flex tw-max-w-3xl tw-w-full tw-justify-between tw-items-center ">
                        {currentDocumentIndex === 0 && currentQuestionIndex === 0 ? (
                            <button
                                onClick={() => navigate("/documents/templates")}
                                className={`tw-bg-gray-200 tw-text-gray-700 tw-rounded-md tw-px-[40px] tw-py-[7px] tw-opacity-50`}
                            >
                                Prev
                            </button>
                        ) : (
                            <button onClick={onPrev} className={`tw-bg-white tw-text-gray-700 tw-rounded-md tw-px-[40px] tw-py-[7px]`}>
                                Prev
                            </button>
                        )}

                        <span className="tw-text-lg">
                            {getCurrentQuestionNumber()}/{totalQuestions}
                        </span>
                        <button
                            onClick={onNext}
                            className="tw-bg-primary hover:tw-bg-primary_hover tw-text-white tw-rounded-md tw-px-[40px] tw-py-[7px]"
                            disabled={isAnswerLoading}
                        >
                            {isAnswerLoading
                                ? "Loading..."
                                : currentQuestionIndex === currentDocument?.questions?.length - 1 &&
                                    currentDocumentIndex === questionsArray.length - 1
                                  ? "Submit"
                                  : "Next"}
                        </button>
                    </div>
                </div>
            ) : (
                <div className="tw-w-full tw-h-full gap-3 tw-flex tw-flex-col tw-justify-center tw-items-center">
                    <h3>No Questions Available for the selected document</h3>
                    <button
                        onClick={() => navigate("/documents/templates")}
                        className="tw-bg-primary hover:tw-bg-primary_hover tw-text-white tw-rounded-md tw-px-[40px] tw-py-[7px]"
                    >
                        Back
                    </button>
                </div>
            )}
        </div>
    );
}
